import React, { useRef, useMemo, useState } from 'react';
import { RadixInput, RadixButton, RadixTextarea, RadixFileUpload, RadixDialog } from '@/components/radix';
import { commonTemplateKeywords } from '@/data/Marketing/MarketingCondition';
import { t } from 'i18next';
import CustomRichTextEditor, { type CustomRichTextEditorHandle } from '@/components/Marketing/CustomRichTextEditor';
import { cnMerge } from '@/utils/cnMerge';
import {
    deleteMarketingAttachmentsApi,
    getMarketingAttachmentHref,
    normalizeMarketingAttachments,
    uploadMarketingAttachmentsApi,
    type MarketingContentAttachment,
} from '@/utils/Api/MarketingAttachments';
import { toast } from 'sonner';
import DeleteIcon from '@/assets/Delete.svg';

const MAX_ATTACHMENT_FILES = 5;
const MAX_ATTACHMENT_TOTAL_SIZE_MB = 5;
const MAX_ATTACHMENT_TOTAL_SIZE_BYTES = MAX_ATTACHMENT_TOTAL_SIZE_MB * 1024 * 1024;

export interface ContentBoxValues {
    sender: string;
    subject: string;
    content: string;
    attachments?: MarketingContentAttachment[];
}

export interface ContentBoxProps {
    // Values
    values: ContentBoxValues;
    // Change handlers
    onSenderChange?: (value: string) => void;
    onSubjectChange?: (value: string) => void;
    onContentChange: (value: string) => void;
    onAttachmentsChange?: (attachments: MarketingContentAttachment[]) => void;
    // Configuration
    triggerType?: 'EMAIL' | 'SMS';
    isReadOnly?: boolean;
    isDisabled?: boolean;
    // Sender Name & Reply To display mode
    senderMode?: 'editable' | 'static';
    // Validation errors (optional, for formik integration)
    errors?: {
        sender?: string;
        subject?: string;
        content?: string;
    };
    touched?: {
        sender?: boolean;
        subject?: boolean;
        content?: boolean;
    };
    // Variable generation
    conditionIds?: number[]; // Direct condition IDs
    triggerGroups?: Array<{ conditions: Array<{ conditionId?: number }> }>; // For trigger groups
    selectedGroups?: string[]; // For campaign selectedGroups
    // Styling
    className?: string;
    showVariables?: boolean; // Whether to show variable tags
    showToolbar?: boolean; // Whether to show toolbar
}

const ContentBox: React.FC<ContentBoxProps> = ({
    values,
    onSenderChange,
    onSubjectChange,
    onContentChange,
    onAttachmentsChange,
    triggerType = 'EMAIL',
    isReadOnly = false,
    isDisabled = false,
    senderMode = 'editable',
    errors,
    touched,
    conditionIds,
    triggerGroups,
    selectedGroups,
    className = '',
    showVariables = true,
    showToolbar = true,
}: ContentBoxProps) => {
    const contentEditorRef = useRef<CustomRichTextEditorHandle>(null);
    const smsTextareaRef = useRef<HTMLTextAreaElement>(null);
    const isSMSCampaign = triggerType === 'SMS';
    const disabled = isReadOnly || isDisabled;
    const attachments = normalizeMarketingAttachments(values.attachments);
    const showAttachments = !isSMSCampaign;

    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<MarketingContentAttachment | null>(null);
    const [isDeleteAttachmentModalOpen, setIsDeleteAttachmentModalOpen] = useState(false);

    // Calculate SMS count based on character length
    // 1-130 chars = 1 SMS, 131-260 = 2 SMS, 261-390 = 3 SMS, etc.
    const calculateSMSCount = (text: string): number => {
        if (!text || text.trim().length === 0) return 0;
        // Strip HTML tags to get actual text length for SMS
        const textContent = text.replace(/<[^>]*>/g, '').trim();
        const charCount = textContent.length;
        if (charCount === 0) return 0;
        // Each SMS can hold 130 characters
        return Math.ceil(charCount / 130);
    };

    const smsCount = useMemo(() => {
        if (!isSMSCampaign) return 0;
        return calculateSMSCount(values.content);
    }, [isSMSCampaign, values.content]);

    const smsCharacterCount = useMemo(() => {
        if (!isSMSCampaign) return 0;
        // Strip HTML tags to get actual text length
        return values.content.replace(/<[^>]*>/g, '').trim().length;
    }, [isSMSCampaign, values.content]);

    const insertVariable = (variable: string) => {
        if (disabled) return;

        if (isSMSCampaign && smsTextareaRef.current) {
            const el = smsTextareaRef.current;
            const start = el.selectionStart ?? values.content.length;
            const end = el.selectionEnd ?? values.content.length;
            const scrollTop = el.scrollTop;

            const nextValue = values.content.slice(0, start) + variable + values.content.slice(end);

            onContentChange(nextValue);

            // Restore cursor after insert (after rerender)
            const nextCursor = start + variable.length;
            requestAnimationFrame(() => {
                try {
                    el.focus();
                    el.setSelectionRange(nextCursor, nextCursor);
                    el.scrollTop = scrollTop;
                } catch {
                    // ignore selection errors (e.g., when element is unmounted)
                }
            });
            return;
        }

        if (contentEditorRef.current) {
            contentEditorRef.current.insertTextAtCursor(variable);
        } else {
            onContentChange(values.content + variable);
        }
    };

    const handleSenderChange = (value: string) => {
        if (!disabled && onSenderChange) {
            const capped = isSMSCampaign && value.length > 11 ? value.slice(0, 11) : value;
            onSenderChange(capped);
        }
    };

    const handleSubjectChange = (value: string) => {
        if (!disabled && onSubjectChange) {
            onSubjectChange(value);
        }
    };

    const handleContentChange = (value: string) => {
        if (!disabled) {
            onContentChange(value);
        }
    };

    const handleAttachmentUpload = async (selectedFileList: FileList | null) => {
        if (disabled || !onAttachmentsChange) return;

        const selectedFiles = Array.from(selectedFileList || []);
        if (!selectedFiles.length) return;

        if (attachments.length + selectedFiles.length > MAX_ATTACHMENT_FILES) {
            toast.error(t('Services.MaxAttachmentCount', { count: MAX_ATTACHMENT_FILES }));
            return;
        }

        const existingAttachmentSize = attachments.reduce(
            (total, attachment) => total + (Number(attachment.fileSize) || 0),
            0,
        );
        const selectedSize = selectedFiles.reduce((total, file) => total + file.size, 0);
        if (existingAttachmentSize + selectedSize > MAX_ATTACHMENT_TOTAL_SIZE_BYTES) {
            toast.error(t('Services.MaxAttachmentBatchSize', { size: MAX_ATTACHMENT_TOTAL_SIZE_MB }));
            return;
        }

        setIsUploadingAttachment(true);
        const loadingToastId = toast.loading(t('Services.AttachmentUploading'));

        try {
            const uploadedAttachments = await uploadMarketingAttachmentsApi(selectedFiles);
            if (!uploadedAttachments.length) {
                throw new Error('No attachments returned');
            }

            onAttachmentsChange([...attachments, ...uploadedAttachments]);
            toast.success(t('Services.AttachmentUploadedSuccessfully'), { id: loadingToastId });
        } catch {
            toast.error(t('Services.AttachmentUploadFailed'), { id: loadingToastId });
        } finally {
            setIsUploadingAttachment(false);
        }
    };

    const confirmDeleteAttachment = (attachment: MarketingContentAttachment) => {
        setAttachmentToDelete(attachment);
        setIsDeleteAttachmentModalOpen(true);
    };

    const handleDeleteAttachment = async () => {
        if (!attachmentToDelete?.id || isDeletingAttachment || !onAttachmentsChange) return;

        setIsDeletingAttachment(true);
        const nextAttachments = attachments.filter((attachment) => attachment.id !== attachmentToDelete.id);

        try {
            const deleteRequest = deleteMarketingAttachmentsApi([Number(attachmentToDelete.id)]).catch((error) => {
                const statusCode =
                    (error as { response?: { status?: number }; status?: number })?.response?.status ??
                    (error as { status?: number })?.status;
                if (statusCode === 404) return { alreadyDeleted: true };
                throw error;
            });

            await toast.promise(deleteRequest, {
                loading: t('Services.AttachmentDeleting'),
                success: t('Services.AttachmentDeletedSuccessfully'),
                error: t('Services.AttachmentDeleteFailed'),
            });

            onAttachmentsChange(nextAttachments);
            setIsDeleteAttachmentModalOpen(false);
            setAttachmentToDelete(null);
        } catch {
            // error toast handled by toast.promise
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    return (
        <div
            className={cnMerge(
                'flex flex-col gap-',
                isReadOnly && 'p-2 border-[1px] border-solid border-border-default rounded-md',
                className,
            )}
        >
            {/* Sender Name */}
            <div className="flex items-center">
                <span
                    className={cnMerge(
                        'text-sm text-text-primary whitespace-nowrap',
                        touched?.sender && errors?.sender ? 'mb-6' : '',
                    )}
                >
                    {t('Marketing.EmailCampaignsSenderName')} :
                </span>

                <div className="w-full flex items-center gap-1">
                    {senderMode === 'editable' && !isReadOnly ? (
                        <div className="w-full flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-1">
                                <RadixInput
                                    value={values.sender}
                                    onChange={(e) => handleSenderChange(e.target.value)}
                                    onBlur={() => {}}
                                    placeholder="Beauty"
                                    className="w-full h-10 border-none px-0"
                                    disabled={disabled}
                                    maxLength={isSMSCampaign ? 11 : undefined}
                                />
                                {isSMSCampaign && (
                                    <span className="text-xs text-text-secondary whitespace-nowrap">
                                        {values.sender.length}/11 characters
                                    </span>
                                )}
                            </div>
                            {touched?.sender && errors?.sender && (
                                <p className="m-0 text-xs text-error-500">{errors.sender}</p>
                            )}
                        </div>
                    ) : isReadOnly ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-background-subtle text-text-primary border border-border-default">
                            {values?.sender ?? 'N/A'}
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-text-secondary"
                            >
                                <path
                                    d="M9 3L3 9M3 3L9 9"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-background-subtle text-text-primary border border-border-default">
                            {values?.sender ?? 'N/A'}
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-text-secondary"
                            >
                                <path
                                    d="M9 3L3 9M3 3L9 9"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    )}
                </div>
            </div>
            {isReadOnly && <div className="h-px w-full bg-border-default my-2" />}
            {isReadOnly && !isSMSCampaign && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-text-primary whitespace-nowrap">{t('Common.Subject')} :</span>
                    <p className="text-base text-text-primary my-2">{values?.subject ?? 'N/A'}</p>
                </div>
            )}

            {!isReadOnly && !isSMSCampaign && <div className="h-[1px] bg-border-default m-0 p-0" />}

            {/* Subject (Email only) */}
            {!isSMSCampaign && !isReadOnly && (
                <div className="flex items-center w-full">
                    <span className="text-sm text-text-primary whitespace-nowrap">{t('Common.Subject')} :</span>
                    <div className="flex flex-col gap-1 w-full">
                        <RadixInput
                            value={values.subject}
                            onChange={(e) => handleSubjectChange(e.target.value)}
                            onBlur={() => {}}
                            placeholder="15% off facials"
                            className="w-full h-10 border-none px-0 "
                            disabled={disabled}
                        />
                        {touched?.subject && errors?.subject && (
                            <p className="m-0 text-xs text-error-500">{errors.subject}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className={cnMerge(!isReadOnly && 'flex-1 flex flex-col min-h-0')}>
                {isSMSCampaign ? (
                    <RadixTextarea
                        ref={smsTextareaRef}
                        value={values.content}
                        onChange={(e) => handleContentChange(e.target.value)}
                        placeholder={t('Marketing.EnterSMSContent')}
                        readOnly={isReadOnly}
                        disabled={disabled}
                        className={cnMerge(
                            'w-full min-h-[160px] resize-y px-3 py-2 text-sm text-text-primary bg-transparent outline-none',
                            'placeholder:text-text-tertiary',
                            'rounded-md',
                            'disabled:cursor-not-allowed disabled:opacity-60',
                        )}
                    />
                ) : (
                    <CustomRichTextEditor
                        ref={contentEditorRef}
                        value={values.content}
                        onChange={handleContentChange}
                        placeholder={t('Marketing.EnterEmailContent')}
                        readOnly={isReadOnly}
                        disabled={disabled}
                        showToolbar={showToolbar}
                    />
                )}
                {touched?.content && errors?.content && (
                    <p className="m-0 text-xs ml-2 text-error-500 mt-1">{errors.content}</p>
                )}
                {/* SMS Character Counter */}
                {isSMSCampaign && (
                    <div className="px-3 py-2 border-t border-[#eaeaea] flex items-center justify-end gap-2">
                        <span className="text-xs text-text-secondary">
                            {smsCharacterCount} {smsCharacterCount === 1 ? 'character' : 'characters'}
                        </span>
                        {smsCount > 0 && (
                            <span className="text-xs font-medium text-text-primary">
                                {smsCount} {smsCount === 1 ? 'SMS' : 'SMS'}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Attachments (Email only) — under rich text editor */}
            {showAttachments && (
                <div className="mt-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <h4 className="m-0 text-sm font-medium text-text-primary">{t('Calendar.UploadedFiles')}</h4>
                    </div>

                    {!disabled && onAttachmentsChange && (
                        <RadixFileUpload
                            onFilesSelected={handleAttachmentUpload}
                            multiple
                            disabled={isUploadingAttachment || attachments.length >= MAX_ATTACHMENT_FILES}
                            buttonText={isUploadingAttachment ? t('Common.Uploading') : t('Common.Upload')}
                            helperText={t('Services.AttachmentUploadLimit', {
                                count: MAX_ATTACHMENT_FILES,
                                size: MAX_ATTACHMENT_TOTAL_SIZE_MB,
                            })}
                            className="mb-2"
                            dropzoneClassName="min-h-[140px]"
                        />
                    )}

                    {attachments.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {attachments.map((attachment) => (
                                <div
                                    key={attachment.id}
                                    className="flex items-center justify-between gap-2 border border-border-default rounded-md px-3 py-2"
                                >
                                    <a
                                        href={getMarketingAttachmentHref(attachment.url)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm truncate text-primary-600"
                                    >
                                        {attachment.fileName}
                                    </a>
                                    {!disabled && onAttachmentsChange && (
                                        <RadixButton
                                            variant="outline"
                                            iconOnly
                                            size="xs"
                                            title={t('Common.Delete')}
                                            onClick={() => confirmDeleteAttachment(attachment)}
                                            disabled={isDeletingAttachment}
                                            className="shrink-0 p-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0"
                                        >
                                            <img src={DeleteIcon} alt={t('Common.Delete')} className="w-4 h-4" />
                                        </RadixButton>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        !disabled && <p className="text-gray-500 text-sm m-0">{t('Marketing.NoAttachments')}</p>
                    )}
                </div>
            )}

            {/* Variable Tags */}
            {showVariables && !disabled && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {commonTemplateKeywords.map((variable) => (
                        <RadixButton
                            key={variable}
                            onClick={() => insertVariable(`{${variable}} `)}
                            variant="outline"
                            size="xs"
                            className="!bg-[#fff4e9]  !border-none min-w-fit !py-0 !px-0 !h-auto m-0"
                        >
                            {`{${variable}}`}
                        </RadixButton>
                    ))}
                </div>
            )}

            <RadixDialog
                overlayClassName="z-[9999]"
                contentClassName="z-[99999]"
                open={isDeleteAttachmentModalOpen}
                onOpenChange={(open) => {
                    if (isDeletingAttachment) return;
                    setIsDeleteAttachmentModalOpen(open);
                    if (!open) {
                        setAttachmentToDelete(null);
                    }
                }}
                title={t('Services.DeleteAttachmentTitle')}
                description={t('Services.DeleteAttachmentDescription')}
                footer={
                    <div className="flex items-center gap-2">
                        <RadixButton
                            variant="outline"
                            disabled={isDeletingAttachment}
                            onClick={() => {
                                setIsDeleteAttachmentModalOpen(false);
                                setAttachmentToDelete(null);
                            }}
                        >
                            {t('Setting.Cancel')}
                        </RadixButton>
                        <RadixButton variant="danger" onClick={handleDeleteAttachment} disabled={isDeletingAttachment}>
                            {t('Common.Delete')}
                        </RadixButton>
                    </div>
                }
            />
        </div>
    );
};

ContentBox.displayName = 'ContentBox';
export default ContentBox;
